import { useCallback, useEffect, useRef, useState } from "react";
import { Star, Download, Copy, Wand2, Lightbulb, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDownload, FORMATS, libError, retryToast } from "@/lib/libraryApi";
import RenderPanel from "@/components/library/RenderPanel";
import LevelLadder from "@/components/library/LevelLadder";

export default function TemplateDetail({ id, onClose, collections, onChanged }) {
  const { t } = useI18n();
  const download = useDownload();
  const [cur, setCur] = useState(id);
  const [trail, setTrail] = useState([]);
  const [d, setD] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const drafts = useRef({});
  useEffect(() => { setCur(id); setTrail([]); if (!id) drafts.current = {}; }, [id]);
  const load = useCallback(() => {
    if (!cur) return setD(null);
    setD(null); setLoadErr(null);
    api.get(`/library/missions/${cur}`).then((r) => setD(r.data)).catch((e) => setLoadErr(libError(e, t, t("lib.loadFailed"))));
  }, [cur, t]);
  useEffect(load, [load]);
  const m = d?.mission;
  const save = async () => {
    try { const r = await api.post(`/library/missions/${cur}/save`); setD({ ...d, saved: r.data.saved }); onChanged?.(); }
    catch (e) { retryToast(libError(e, t), t, save); }
  };
  const addTo = async (cid) => {
    try { await api.post(`/library/collections/${cid}/items`, { kind: "template", ref_id: cur }); toast.success(t("lib.added")); onChanged?.(); }
    catch (e) { retryToast(libError(e, t, t("lib.collectionFailed")), t, () => addTo(cid)); }
  };
  const openVersion = (mid) => { setTrail([...trail, { id: cur, title: m.title }]); setCur(mid); };
  const back = () => { const p = trail[trail.length - 1]; setTrail(trail.slice(0, -1)); setCur(p.id); };
  const Row = ({ k, v }) => v ? <div><div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{t(`lib.f.${k}`)}</div><p className="text-sm" dir="ltr">{v}</p></div> : null;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lib-detail"
        onInteractOutside={(e) => { if (e.target?.closest?.("[data-sonner-toaster]")) e.preventDefault(); }}>
        {loadErr ? (
          <div className="py-8 text-center space-y-3" data-testid="lib-detail-error">
            <DialogTitle className="sr-only">{t("lib.loadFailed")}</DialogTitle>
            <p className="text-sm">{loadErr}</p>
            <button onClick={load} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="lib-detail-retry">{t("lib.retry")}</button>
          </div>
        ) : !m ? <div className="h-40 animate-pulse"><DialogTitle className="sr-only">{t("lib.title")}</DialogTitle></div> : (
          <>
            {trail.length > 0 && (
              <button onClick={back} className="text-xs font-bold flex items-center gap-1 text-gray-600" data-testid="lib-ladder-back">
                <ArrowLeft className="w-3 h-3 rtl:rotate-180" /> {t("lib.backTo")} <bdi>{trail[trail.length - 1].title}</bdi>
              </button>
            )}
            <DialogHeader className="text-start sm:text-start">
              <DialogTitle className="font-cabinet text-2xl pe-8" data-testid="lib-detail-title"><bdi>{m.title}</bdi></DialogTitle>
              <div className="flex flex-wrap gap-1.5 text-xs mt-1">
                <span className="nb-tag bg-mint" data-testid="lib-detail-cefr">{m.cefr}</span><span className="nb-tag bg-sky">{m.communication_mode}</span>
                <span className="nb-tag bg-butter">{m.primary_skill}</span><span className="nb-tag bg-white">{m.duration_min} min · {m.grouping}</span>
                <span className="nb-tag bg-white" dir="ltr">{m.mission_id}</span>
              </div>
            </DialogHeader>
            <div className="nb-card bg-lavender p-4" data-testid="lib-why">
              <div className="font-bold flex items-center gap-2"><Lightbulb className="w-4 h-4" /> {t("lib.why")}</div>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <Row k="cefr_can_do" v={d.why.cefr_can_do} /><Row k="curriculum_strand" v={d.why.curriculum_strand} />
                <Row k="evidence" v={d.why.evidence} /><Row k="gy25" v={d.why.gy25} />
              </div>
              <p className="text-xs text-gray-600 mt-2">{t("lib.whyNote")}</p>
            </div>
            <LevelLadder current={m} ladder={d.ladder} onOpen={openVersion} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Row k="learning_objective" v={m.learning_objective} /><Row k="mission_brief_master" v={m.mission_brief_master} />
              <Row k="learner_output" v={m.learner_output} /><Row k="success_criteria" v={m.success_criteria} />
              <Row k="scaffold_down" v={m.scaffold_down} /><Row k="stretch_up" v={m.stretch_up} />
              <Row k="fun_deep_hook" v={m.fun_deep_hook} /><Row k="culture_lens" v={m.culture_lens} />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={save} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="lib-save"><Star className={`w-4 h-4 ${d.saved ? "fill-current" : ""}`} /> {d.saved ? t("lib.saved") : t("lib.save")}</button>
              {collections.length > 0 && (
                <select className="nb-input py-1.5 w-auto text-sm" value="" onChange={(e) => e.target.value && addTo(e.target.value)} data-testid="lib-add-collection">
                  <option value="">{t("lib.addTo")}</option>
                  {collections.map((c) => <option key={c.collection_id} value={c.collection_id}>{c.name}</option>)}
                </select>
              )}
              {FORMATS.map((f) => (
                <button key={f} onClick={() => download(`/library/missions/${cur}/export?format=${f}`, `${cur}.${f}`)} className="nb-btn-outline py-1.5 px-3 text-xs uppercase" data-testid={`lib-export-${f}`}><Download className="w-3 h-3" /> {f}</button>
              ))}
              <button onClick={() => { navigator.clipboard.writeText(m.language_render_prompt); toast.success(t("lib.copied")); }} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="lib-copy-prompt"><Copy className="w-3 h-3" /> {t("lib.copyPrompt")}</button>
            </div>
            <div className="border-t-2 border-ink/10 pt-4">
              <h3 className="font-cabinet font-bold text-lg flex items-center gap-2"><Wand2 className="w-5 h-5" /> {t("lib.remixTitle")}</h3>
              <RenderPanel key={cur} templateId={cur} memo={drafts.current} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
